import { default as Vuex, Module, ActionContext } from "vuex";
import * as Models from "@gigalot/data-models";
import Uploadable, { UploadableScanningResult } from "@/models/uploadable";
import { getHhMmSs, getYyyyMmDd } from "@/helpers/date-time";
import { geolocation } from "@/helpers/geolocation";

class GroupTreatmentState {
  batchNumber?: string
  batchQuantity?: number
  notes?: string
  ailments?: Models.Ailment[]
  appliedTreatment?: Models.AppliedTreatment
  kraalId?: string
  averageMass?: number
}

export default new (class GroupTreatment implements Module<GroupTreatmentState, any> {
  namespaced = true;
  state: GroupTreatmentState = new GroupTreatmentState();
  mutations = {
    /*
    mutation(state: State, payload: any) {
      //no async calls
      state.data = payload;
    }
    */
    batchNumber(state: GroupTreatmentState, batchNumber?: string) {
      state.batchNumber = batchNumber;
    },
    batchQuantity(state: GroupTreatmentState, batchQuantity?: number) {
      state.batchQuantity = batchQuantity;
    },
    notes(state: GroupTreatmentState, notes?: string) {
      state.notes = notes;
    },
    ailments(state: GroupTreatmentState, ailments?: Models.Ailment[]) {
      state.ailments = ailments;
    },
    appliedTreatment(state: GroupTreatmentState, appliedTreatment?: Models.AppliedTreatment) {
      state.appliedTreatment = appliedTreatment;
      //console.log("appliedTreatment:", JSON.stringify(appliedTreatment))
    },
    kraalId(state: GroupTreatmentState, kraalId?: string) {
      state.kraalId = kraalId;
    },
    averageMass(state: GroupTreatmentState, averageMass?: number) {
      state.averageMass = averageMass;
    },
  };
  actions = {
    /*
    action(context: ActionContext<State, any>) {
      //async calls allowed, action can also be async
      //context.state, context.rootState, context.dispatch, context.commit
    }
    */
    async save(context: ActionContext<GroupTreatmentState, any>) {
      if (!context.state.batchNumber) {
        throw Error("No batch reference entered");
      }
      if (context.state.batchQuantity === undefined) {
        throw Error("No quantity entered");
      } else if (context.state.batchQuantity <= 0 || isNaN(context.state.batchQuantity)) {
        throw Error("Invalid batch quantity");
      }
      if (!context.state.appliedTreatment) {
        throw Error("No treatment selected");
      }
      if (context.state.kraalId) {
        const kraalIds: string[] = await context.dispatch("data/getKraalIds", undefined, { root: true });
        if (kraalIds.find((kraalId) => kraalId === context.state.kraalId) === undefined) {
          throw Error("Invalid kraal");
        }
      }
      let metadata: Models.UpstreamMetadata = context.rootGetters["upload/getUpstreamMetadata"]();
      let groupTreatment: UploadableScanningResult = Object.assign(new Models.ScanningResult("group-treatment", Date.now(), metadata), new Uploadable());
      groupTreatment.tags = context.rootState.scan.tags;

      const numTags = groupTreatment.tags.length;
      if (context.state.batchQuantity < numTags) {
        throw Error("Quantity can not be lower than number of tags scanned.");
      }
      groupTreatment.batchQuantity = "" + context.state.batchQuantity;

      if (!context.state.batchNumber) {
        context.commit("batchNumber", `${getYyyyMmDd(new Date(groupTreatment.time)).slice(2)}${getHhMmSs(new Date(groupTreatment.time))}`);
      }

      //try get geolocation
      try {
        const p = await geolocation({ enableHighAccuracy: false, maximumAge: 0, timeout: 15 * 1000 });
        groupTreatment.gpsLat = p.coords.latitude.toString();
        groupTreatment.gpsLon = p.coords.longitude.toString();
      } catch (err) {
        console.error("Could not get geolocation: ", err);
      }

      groupTreatment.batchNumber = context.state.batchNumber || "";

      groupTreatment.notes = context.state.notes;

      groupTreatment.ailments = context.state.ailments ?? [];

      groupTreatment.treatment = context.state.appliedTreatment
      groupTreatment.kraalId = context.state.kraalId || "";
      groupTreatment.averageMass = context.state.averageMass;

      groupTreatment.generateAutoBatchNumber();
      await context.dispatch("data/addGroupTreatment", groupTreatment, { root: true });
      context.commit("upload/numYetToUpload", "increment", { root: true });
      //throw Error("Some error here blah blah"); //uncomment this line to test Save popup displaying error
      try {
        context.dispatch("upload/upload", undefined, { root: true });
      } catch (e) {
        //silent failure is fine here, upload will likely fail here anyway if the user is still on the scanner's wifi
      }
    }
  };
  getters = {
    /*
    getter(state: State, getters: any, rootState: any, rootGetters: any) {
      //return a function if you want the getter to receive input parameters
    }
    */
  };
})();
